import UserPermissionTypes from '@constants/permissions';
import { getMergeLabel } from '@features/team-management/constants/permissionUiConfig';

export interface PermissionCatalogEntry {
  label: string;
  module: string;
}

/** UI module headings for custom-role permission lists. */
const MODULE = {
  BRANCHES: 'Branches',
  MEMBERS: 'Members',
  GROUPS: 'Groups',
  PAYMENTS: 'Payments',
  REPORTS: 'Reports',
  EXPENSE_TRACKER: 'Expense tracker',
  ORGANISATION_SETTINGS: 'Organisation Settings',
  DASHBOARD: 'Dashboard',
  EVENTS: 'Events',
  LEAD_MANAGEMENT: 'Lead management',
  FORMS: 'Forms',
  TEAM_MANAGEMENT: 'Team management',
  KYC: 'KYC',
  ESIGN: 'E-Sign',
  INTEGRATIONS: 'Integrations',
  OTHER: 'Other'
} as const;

export const humanisePermissionSlug = (slug: string): string =>
  slug
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/** Labels and modules for custom-role create/view checklists. */
const PERMISSION_CATALOG: Record<string, PermissionCatalogEntry> = {
  [UserPermissionTypes.BRANCH_EDIT]: {
    label: 'View/Edit a branch settings',
    module: MODULE.BRANCHES
  },
  [UserPermissionTypes.ACCOUNT_DETAILS_VIEW]: {
    label: 'View bank list',
    module: MODULE.BRANCHES
  },
  [UserPermissionTypes.ACCOUNT_DETAILS_SENSITIVE_DATA_VIEW]: {
    label: 'View bank account number',
    module: MODULE.BRANCHES
  },
  [UserPermissionTypes.BRANCH_CREATE]: {
    label: 'Create a branch',
    module: MODULE.BRANCHES
  },
  [UserPermissionTypes.BRANCH_VIEW]: {
    label: 'View branch details',
    module: MODULE.BRANCHES
  },
  [UserPermissionTypes.BRANCH_SUMMARY_VIEW]: {
    label: 'Branch summary view',
    module: MODULE.BRANCHES
  },
  [UserPermissionTypes.BRANCH_MEMBER_VIEW]: {
    label: 'View member list',
    module: MODULE.MEMBERS
  },
  [UserPermissionTypes.BRANCH_MEMBER_CREATE]: {
    label: 'Create a member in member list',
    module: MODULE.MEMBERS
  },
  [UserPermissionTypes.BRANCH_MEMBER_EDIT]: {
    label: 'Edit a member in member list',
    module: MODULE.MEMBERS
  },
  [UserPermissionTypes.BRANCH_MEMBER_DELETE]: {
    label: 'Delete a member in member list',
    module: MODULE.MEMBERS
  },
  [UserPermissionTypes.GROUP_VIEW]: {
    label: 'View group details',
    module: MODULE.GROUPS
  },
  [UserPermissionTypes.GROUP_PAYMENT_SUMMARY_VIEW]: {
    label: 'View group payment summary',
    module: MODULE.GROUPS
  },
  [UserPermissionTypes.GROUP_CREATE]: {
    label: 'Create a group',
    module: MODULE.GROUPS
  },
  [UserPermissionTypes.GROUP_EDIT]: {
    label: 'Edit a group',
    module: MODULE.GROUPS
  },
  [UserPermissionTypes.GROUP_DISABLE]: {
    label: 'Disable a group',
    module: MODULE.GROUPS
  },
  [UserPermissionTypes.GROUP_MEMBER_VIEW]: {
    label: 'View group members',
    module: MODULE.GROUPS
  },
  [UserPermissionTypes.GROUP_MEMBER_CREATE]: {
    label: 'Create a group member',
    module: MODULE.GROUPS
  },
  [UserPermissionTypes.GROUP_MEMBER_EDIT]: {
    label: 'Edit/Delete/Disable a group member',
    module: MODULE.GROUPS
  },
  [UserPermissionTypes.GROUP_ATTENDANCE_VIEW]: {
    label: 'View attendance',
    module: MODULE.GROUPS
  },
  [UserPermissionTypes.GROUP_ATTENDANCE_EDIT]: {
    label: 'Mark attendance',
    module: MODULE.GROUPS
  },
  [UserPermissionTypes.GROUP_ATTENDANCE_REPORT_DOWNLOAD]: {
    label: 'Download attendance report',
    module: MODULE.GROUPS
  },
  [UserPermissionTypes.PAYMENT_ORDER_GENERATE_GROUP_CUSTOM_REPORT]: {
    label: 'Download group monthly report',
    module: MODULE.GROUPS
  },
  [UserPermissionTypes.MEMBER_CONTACT_VIEW]: {
    label: 'View member contact details',
    module: MODULE.MEMBERS
  },
  [UserPermissionTypes.OWNER_VIEW]: {
    label: 'View owner details',
    module: MODULE.MEMBERS
  },
  [UserPermissionTypes.PAYMENT_ORDER_VIEW]: {
    label: 'View transaction details',
    module: MODULE.PAYMENTS
  },
  [UserPermissionTypes.PAYMENT_ORDER_CREATE]: {
    label: 'Send quick collect (branch and group)',
    module: MODULE.PAYMENTS
  },
  [UserPermissionTypes.PAYMENT_ORDER_MARK_PAID]: {
    label: 'Mark payments as paid',
    module: MODULE.PAYMENTS
  },
  [UserPermissionTypes.PAYMENT_ORDER_SPLIT_ENABLE]: {
    label: 'Enable Partial Payment',
    module: MODULE.PAYMENTS
  },
  [UserPermissionTypes.PAYMENT_ORDER_DISCOUNT_CREATE]: {
    label: 'Apply discount on transaction',
    module: MODULE.PAYMENTS
  },
  [UserPermissionTypes.PAYMENT_ORDER_DISABLE]: {
    label: 'Disable payment link',
    module: MODULE.PAYMENTS
  },
  [UserPermissionTypes.PAYMENT_ORDER_CLOSE]: {
    label: 'Close payment due',
    module: MODULE.PAYMENTS
  },
  // Legacy master — hidden in UI; payload AND of split actions.
  [UserPermissionTypes.PAYMENT_ORDER_EDIT]: {
    label: 'Edit payments (master permission -BE)',
    module: MODULE.PAYMENTS
  },
  [UserPermissionTypes.PAYMENT_ORDER_GENERATE_RECEIPT]: {
    label: 'Download receipt',
    module: MODULE.PAYMENTS
  },
  [UserPermissionTypes.PAYMENT_ORDER_SEND_REMINDER]: {
    label: 'Send payment reminder',
    module: MODULE.PAYMENTS
  },
  [UserPermissionTypes.PAYMENT_ORDER_GENERATE_GST_REPORT]: {
    label: 'Download GST report',
    module: MODULE.REPORTS
  },
  [UserPermissionTypes.PAYMENT_ORDER_GENERATE_CUSTOM_REPORT]: {
    label: 'Download collections report',
    module: MODULE.REPORTS
  },
  [UserPermissionTypes.PAYMENT_ORDER_GENERATE_REPORT]: {
    label: 'Download collections report',
    module: MODULE.REPORTS
  },
  [UserPermissionTypes.EXPENSE_VIEW]: {
    label: 'View expenses',
    module: MODULE.EXPENSE_TRACKER
  },
  [UserPermissionTypes.EXPENSE_CREATE]: {
    label: 'Create expense',
    module: MODULE.EXPENSE_TRACKER
  },
  [UserPermissionTypes.EXPENSE_STATUS_CHANGE]: {
    label: 'Approve/Reject expense',
    module: MODULE.EXPENSE_TRACKER
  },
  [UserPermissionTypes.EXPENSE_DELETE]: {
    label: 'Delete expense',
    module: MODULE.EXPENSE_TRACKER
  },
  [UserPermissionTypes.ORGANISATION_SETTINGS_EDIT]: {
    label: 'View/Edit organisation configuration',
    module: MODULE.ORGANISATION_SETTINGS
  },
  [UserPermissionTypes.CUSTOMER_LIST_VIEW]: {
    label: 'View organisation customer list',
    module: MODULE.ORGANISATION_SETTINGS
  },
  [UserPermissionTypes.CUSTOMER_DETAILS_VIEW]: {
    label: 'View organisation config',
    module: MODULE.ORGANISATION_SETTINGS
  },
  [UserPermissionTypes.CUSTOMER_DETAILS_EDIT]: {
    label: 'Edit organisation config',
    module: MODULE.ORGANISATION_SETTINGS
  },
  [UserPermissionTypes.DASHBOARD_VIEW]: {
    label: 'View branch dashboard',
    module: MODULE.DASHBOARD
  },
  [UserPermissionTypes.ORG_DASHBOARD_VIEW]: {
    label: 'View organization dashboard',
    module: MODULE.DASHBOARD
  },
  [UserPermissionTypes.EVENT_VIEW]: {
    label: 'View event details',
    module: MODULE.EVENTS
  },
  [UserPermissionTypes.EVENT_ATTENDEE_LIST]: {
    label: 'View event attendees',
    module: MODULE.EVENTS
  },
  [UserPermissionTypes.EVENT_ATTENDEE_VIEW]: {
    label: 'View event attendees',
    module: MODULE.EVENTS
  },
  [UserPermissionTypes.EVENT_CREATE]: {
    label: 'Create event',
    module: MODULE.EVENTS
  },
  [UserPermissionTypes.EVENT_EDIT]: {
    label: 'Edit event',
    module: MODULE.EVENTS
  },
  [UserPermissionTypes.EVENT_STATUS_CHANGE]: {
    label: 'Change event status',
    module: MODULE.EVENTS
  },
  [UserPermissionTypes.EVENT_TICKET_STATUS_CHANGE]: {
    label: 'Change event ticket status',
    module: MODULE.EVENTS
  },
  [UserPermissionTypes.EVENT_BRANCH_VIEW]: {
    label: 'View event branch',
    module: MODULE.EVENTS
  },
  [UserPermissionTypes.EVENT_QR_ATTENDEE_VIEW]: {
    label: 'Check in attendee',
    module: MODULE.EVENTS
  },
  [UserPermissionTypes.EVENT_BOOKING_ID_READ]: {
    label: 'Check in attendee',
    module: MODULE.EVENTS
  },
  [UserPermissionTypes.EVENT_QR_ATTENDEE_CHECK_IN]: {
    label: 'Check in attendee',
    module: MODULE.EVENTS
  },
  [UserPermissionTypes.LEAD_MANAGEMENT_VIEW]: {
    label: 'View lead details',
    module: MODULE.LEAD_MANAGEMENT
  },
  [UserPermissionTypes.LEAD_MANAGEMENT_CREATE]: {
    label: 'Create lead',
    module: MODULE.LEAD_MANAGEMENT
  },
  [UserPermissionTypes.LEAD_BULK_UPLOAD]: {
    label: 'Create lead',
    module: MODULE.LEAD_MANAGEMENT
  },
  [UserPermissionTypes.LEAD_MANAGEMENT_EDIT]: {
    label: 'Edit lead',
    module: MODULE.LEAD_MANAGEMENT
  },
  [UserPermissionTypes.LEAD_REPORT_GENERATE]: {
    label: 'Download lead report',
    module: MODULE.LEAD_MANAGEMENT
  },
  [UserPermissionTypes.LEAD_MANAGEMENT_CONFIG_VIEW]: {
    label: 'View lead settings',
    module: MODULE.LEAD_MANAGEMENT
  },
  [UserPermissionTypes.LEAD_MANAGEMENT_CONFIG_EDIT]: {
    label: 'View lead settings',
    module: MODULE.LEAD_MANAGEMENT
  },
  [UserPermissionTypes.FORM_VIEW]: {
    label: 'View forms',
    module: MODULE.FORMS
  },
  [UserPermissionTypes.FORM_SUBMISSION_VIEW]: {
    label: 'View forms',
    module: MODULE.FORMS
  },
  [UserPermissionTypes.FORM_CREATE]: {
    label: 'Create form',
    module: MODULE.FORMS
  },
  [UserPermissionTypes.FORM_EDIT]: {
    label: 'Edit form',
    module: MODULE.FORMS
  },
  [UserPermissionTypes.FORM_STATUS_CHANGE]: {
    label: 'Change form status',
    module: MODULE.FORMS
  },
  [UserPermissionTypes.FORM_EXPORT]: {
    label: 'Export form data',
    module: MODULE.FORMS
  },
  [UserPermissionTypes.ORG_ADMIN_VIEW]: {
    label: 'View organisation admins',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.ORG_ADMIN_CREATE]: {
    label: 'Create organisation admin',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.ORG_ADMIN_EDIT]: {
    label: 'Edit organisation admin',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.BRANCH_ADMIN_VIEW]: {
    label: 'View branch admins',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.BRANCH_ADMIN_CREATE]: {
    label: 'Create a branch admin',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.BRANCH_ADMIN_EDIT]: {
    label: 'Edit a branch admin',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.GROUP_ADMIN_VIEW]: {
    label: 'View group admins',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.GROUP_ADMIN_CREATE]: {
    label: 'Create a group admin',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.GROUP_ADMIN_EDIT]: {
    label: 'Edit a group admin',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.FINANCE_ADMIN_VIEW]: {
    label: 'View finance analyst',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.FINANCE_ADMIN_CREATE]: {
    label: 'Create finance analyst',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.FINANCE_ADMIN_EDIT]: {
    label: 'Edit finance analyst',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.PAYMENT_MANAGER_VIEW]: {
    label: 'View finance manager',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.PAYMENT_MANAGER_CREATE]: {
    label: 'Create finance manager',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.PAYMENT_MANAGER_EDIT]: {
    label: 'Edit finance manager',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.EVENT_MANAGER_VIEW]: {
    label: 'View event managers',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.EVENT_MANAGER_CREATE]: {
    label: 'Create event manager',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.EVENT_MANAGER_EDIT]: {
    label: 'Edit event manager',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.EVENT_VOLUNTEER_VIEW]: {
    label: 'View event volunteers',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.EVENT_VOLUNTEER_CREATE]: {
    label: 'Create event volunteer',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.EVENT_VOLUNTEER_EDIT]: {
    label: 'Edit event volunteer',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.SALES_MANAGER_VIEW]: {
    label: 'View sales managers',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.SALES_MANAGER_CREATE]: {
    label: 'Create sales manager',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.SALES_MANAGER_EDIT]: {
    label: 'Edit sales manager',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.SALES_AGENT_VIEW]: {
    label: 'View sales agents',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.SALES_AGENT_CREATE]: {
    label: 'Create sales agent',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.SALES_AGENT_EDIT]: {
    label: 'Edit sales agent',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.ATTENDANCE_MANAGER_VIEW]: {
    label: 'View attendance managers',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.ATTENDANCE_MANAGER_CREATE]: {
    label: 'Create attendance manager',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.ATTENDANCE_MANAGER_EDIT]: {
    label: 'Edit attendance manager',
    module: MODULE.TEAM_MANAGEMENT
  },
  [UserPermissionTypes.ESIGN_VIEW]: {
    label: 'View e-sign requests',
    module: MODULE.ESIGN
  },
  [UserPermissionTypes.ESIGN_CREATE]: {
    label: 'Create e-sign request',
    module: MODULE.ESIGN
  },
  [UserPermissionTypes.INTEGRATION_EDIT]: {
    label: 'Integrations/APIs',
    module: MODULE.INTEGRATIONS
  },
  [UserPermissionTypes.KYC_DOCUMENT_STATUS_CHANGE]: {
    label: 'Change KYC document status',
    module: MODULE.KYC
  },
  [UserPermissionTypes.KYC_DOCUMENT_VIEW]: {
    label: 'View KYC documents',
    module: MODULE.KYC
  }
};

export const getPermissionCatalogEntry = (
  slug: string
): PermissionCatalogEntry => {
  const entry = PERMISSION_CATALOG[slug];
  const mergeLabel = getMergeLabel(slug);
  if (entry) {
    return mergeLabel ? { ...entry, label: mergeLabel } : entry;
  }
  return {
    label: mergeLabel ?? humanisePermissionSlug(slug),
    module: MODULE.OTHER
  };
};

export { PERMISSION_CATALOG, MODULE as PermissionModule };
